import { ScrollingModule } from "@angular/cdk/scrolling";
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  effect,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { IonicModule, isPlatform } from "@ionic/angular";
import { Store } from "@ngrx/store";
import * as appointmentsSelector from "../../../calendar/ngrx/calendar.selectors";
import { selectStudentById } from "src/app/students/ngrx/students.selectors";
import { Subject, takeUntil } from "rxjs";
import { InvoiceItemDto } from "../../interfaces/invoice_item_dto";
import { DialogService } from "src/app/shared/services/dialog.service";
import { selectSetting } from "src/app/settings/ngrx/settings.action";
import { selectCustomer } from "src/app/auth/ngrx/customer.selectors";
import { loadAppointments } from "src/app/calendar/ngrx/calendar.actions";

@Component({
    selector: "app-open-invoice-list-page",
    templateUrl: "./open-invoice-list-page.component.html",
    styleUrls: ["./open-invoice-list-page.component.scss"],
    imports: [IonicModule, ScrollingModule, FormsModule],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class OpenInvoiceListPageComponent implements OnInit, OnDestroy {
  originPage = isPlatform("ios") ? "Büro" : "";
  section = "pastServices";
  openServicesSignal = this.store.selectSignal(
    appointmentsSelector.selectUnchargedLessons
  );
  customerSignal = this.store.selectSignal(selectCustomer);
  openServices: any;
  groupedServices: any;
  private destroy$ = new Subject<void>();
  @Output() onCloseList = new EventEmitter();

  constructor(private store: Store, private dialogService: DialogService, private cdr: ChangeDetectorRef) {
    effect(() => {
      return this.updateOpenServices(this.openServicesSignal());
    });
  }

  ngOnInit() {}

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ionViewDidLeave() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  refreshList(event: any) {
    this.store.dispatch(loadAppointments());

    this.store
    .select(appointmentsSelector.selectIsLoading)
    .pipe(takeUntil(this.destroy$))
    .subscribe((isLoading) => {
      if (!isLoading) {
        event?.target?.complete();
      }
    });
  }

  updateOpenServices(openServices: any[]) {
    const observables = openServices.map((service) =>
      this.store.select(selectStudentById(service.StudentId))
    );

    if (observables.length === 0) {
      this.openServices = [];
      this.groupedServices = [];
      this.cdr.markForCheck();
      return;
    }

    const studentsData = [];

    observables.forEach((observable, index) => {
      observable.subscribe(
        (student) => {
          // Store the student information along with the open service data
          studentsData.push({
            openService: openServices[index],
            student: student,
          });

          // If all students have been received, update this.openServices
          if (studentsData.length === observables.length) {
            this.applySectionFilter(studentsData);
          }
        },
        (error) => {
          console.error(
            `Error fetching student for ID ${openServices[index].StudentId}:`,
            error
          );
        }
      );
    });
  }

  // Apply section filter based on EndTime for pastServices
  applySectionFilter(studentsData: any[]) {
    // Now you have an array containing both openService and student information
    this.openServices = studentsData.map((data) => {
      const { openService, student } = data;
      return {
        ...openService,
        Student: student,
      };
    });

    // Adjust prize and filter openServices based on EndTime for pastServices
    if (this.section === "pastServices") {
      const currentDate = new Date();
      this.openServices = this.openServices
        .map((service) => {
          const endTime = new Date(service.EndTime);
          if (endTime < currentDate) {
            return service;
          } else {
            return null; // Filter out future services
          }
        })
        .filter((service) => service !== null);
    }

    // group the services by student
    this.groupedServices = this.openServices.reduce((groups, service) => {
      const group = groups.find(
        (g) => g.student.student_id === service.Student?.student_id
      );

      if (group) {
        group.amount_of_services += 1;
        group.total_amount += service.Prize;
        group.services.push(service);
      } else {
        groups.push({
          student: service.Student,
          services: [service],
          total_amount: service.Prize,
          amount_of_services: 1,
        });
      }
      return groups;
    }, []);

    this.cdr.markForCheck();
  }

  async openNewInvoiceDialog(group: any) {
    if (
      this.customerSignal().iban &&
      this.customerSignal().brand &&
      this.customerSignal().city &&
      this.customerSignal().mail_customer &&
      this.customerSignal().postal_code &&
      this.customerSignal().street
    ) {
      let newInvoice = {
        student_id: group.student,
        invoice_items: group.services.map((service) => {
          return {
            id: service.Id,
            appointment_id: service.Id,
            prize: service.Prize,
            name: service.Type || service.Subject,
            date: service.StartTime.toISOString().slice(0, 10),
            amount: service.Lessons,
            unit: service.Lessons > 1 ? "Lektionen" : "Lektion",
            initial_prize: service.Prize,
            subscription_payment: service.SubscriptionPayment,
          } as InvoiceItemDto;
        }),
      };
      this.dialogService.openNewInvoiceDialog(newInvoice);
    } else {
      this.store.dispatch(selectSetting({ setting: "company" }));
      await this.dialogService.openSettings(
        "company",
        "Um diese Funktion nutzen zu können, benötigen wir weitere Informationen. Möchtest du sie in den Einstellungen hinterlegen?"
      );
    }
  }

  close() {
    this.onCloseList.emit();
  }
}
